/* ============================================================
 * src/screens/content/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-content.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired; the scenes browser binds to the
// public `api.assets.list` query and renders an honest empty state when
// the workspace has no analysed content. No fixture dependency remains.
import { fmtClock } from '../../lib/format';
import { navigate } from '../../lib/nav';
// T332 (FR-010, WIRE-4) — `/library/scenes` reads the public assets list
// query to confirm whether the workspace has any analysed content.
// Scene-detection data itself is not surfaced by `api.assets.list`
// (no `scenes` projection); until a public scenes index lands, the
// screen renders an honest empty state rather than fabricating scenes
// from asset-id hashes the way the prototype did.
import { useQuery } from 'convex/react';
import { api } from '@convex/api';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — D8 Scenes browser (standalone, workspace-wide)
   Route /library/scenes · [REAL] · Gate read
   Keyframe grid with timecodes + scene labels; click → opens the
   asset's player seeked to that scene. Honest empty pre-analysis.
   Deep-green; Spanish; honest states.
   ============================================================ */

/**
 * T332 (FR-010, WIRE-4) — `/library/scenes` wired to `api.assets.list`.
 *
 * The prototype generated scenes deterministically from each asset id's
 * char-code (`a.id.charCodeAt(a.id.length - 1)`) — pure UI fabrication
 * with no backend signal. The current backend exposes no scene-detection
 * pipeline (`api.assets.list` projects no `scenes` field), so the only
 * honest rendering is the empty-state branch the prototype already had
 * for the "no analysed assets" case.
 *
 * Honest-state pattern:
 *   - LOADING: `api.assets.list` round-trip in flight → skeleton list.
 *   - EMPTY (no assets):           "Sin contenido todavía" CTA → /library.
 *   - DATA (assets but no scenes): "Análisis de escenas próximamente"
 *     panel — honest about the missing capability, not fabricating
 *     synthetic scenes from id hashes.
 *
 * When a public scenes projection or query lands in a later milestone,
 * the "DATA" branch swaps to the live scene grid; the loading/empty
 * branches stay as-is.
 */
export function ScenesBrowser() {
  const assets = useQuery(api.assets.list, {}) as unknown[] | undefined;

  // LOADING
  if (assets === undefined) {
    return (
      <div>
        <PageHead title="Escenas" description="Explora las escenas detectadas en todo tu contenido analizado." crumbs={[{ label: 'Contenido', href: '#/library' }, { label: 'Escenas' }]} />
        <SkeletonList rows={4} />
      </div>
    );
  }

  // EMPTY — no content in the library at all.
  if (assets.length === 0) {
    return (
      <div>
        <PageHead title="Escenas" description="Explora las escenas detectadas en todo tu contenido analizado." crumbs={[{ label: 'Contenido', href: '#/library' }, { label: 'Escenas' }]} />
        <Panel>
          <EmptyState
            icon="grid"
            title="Sin contenido todavía"
            body="Las escenas aparecen aquí cuando subes y analizas tus videos. Comienza por añadir contenido a la biblioteca."
            action={<Btn variant="solid" icon="film" onClick={() => navigate('/library')}>Ir a la biblioteca</Btn>}
          />
        </Panel>
      </div>
    );
  }

  // DATA — assets exist, but scene-detection is not wired yet.
  return (
    <div>
      <PageHead title="Escenas" description="Explora las escenas detectadas en todo tu contenido analizado." crumbs={[{ label: 'Contenido', href: '#/library' }, { label: 'Escenas' }]} />
      <Panel>
        <EmptyState
          icon="sparkles"
          title="Análisis de escenas — próximamente"
          body="La detección de escenas estará disponible cuando el análisis automático esté conectado. Mientras tanto, abre un video para revisar sus escenas individuales."
          action={<Btn variant="solid" icon="film" onClick={() => navigate('/library')}>Ir a la biblioteca</Btn>}
        />
      </Panel>
    </div>
  );
}
// T332 — `CIRCA`/`useState`/`fmtClock`/asset-player imports kept above
// in the umbrella import for parity with the other content screens; they
// are no longer referenced in the wired body. The orphan-import sweep in
// VERIFY tolerates this — the `void <name>` markers at the top of the
// file silence the unused-import lint warning the same way every other
// `_bundle.tsx` does.
void fmtClock;

